/* eslint-disable max-lines */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellEditingStoppedEvent,
  CellValueChangedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowDragEndEvent,
  RowNode,
  ValueSetterParams,
} from '@ag-grid-community/core';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import isEqual from 'lodash-es/isEqual';
import uniq from 'lodash-es/uniq';
import { BehaviorSubject, combineLatest, firstValueFrom, ReplaySubject } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { SiteCurveQuery } from '@models/site-curve/site-curve.query';
import { SiteCurveService } from '@models/site-curve/site-curve.service';

import { AgActionsComponent } from '@shared/ag-components/ag-actions/ag-actions.component';
import { ButtonComponent } from '@shared/components/button/button.component';
import { ConfirmationActionModalComponent } from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { FormControlConstants } from '@shared/constants/form-control.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TableConstants } from '@shared/constants/table.constants';
import { EventType, Organization } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { AgSetColumnsVisible } from '@shared/utils';
import { RequireSome, Utils } from '@shared/utils/utils';

import { SiteGroupsQuery } from '../../models/site-group/site-groups.query';
import { SiteGroupsService } from '../../models/site-group/site-groups.service';
import { SiteGroupsStore } from '../../models/site-group/site-groups.store';
import { SiteGroupModel } from '../../models/site-group.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, AgGridAngular, AsyncPipe],
  selector: 'aux-forecast-site-groups',
  styles: [
    `
      ::ng-deep .group-actions .ag-cell-value {
        overflow: initial;
      }
    `,
  ],
  templateUrl: 'forecast-site-groups.component.html',
})
export class ForecastSiteGroupsComponent {
  overlayService = inject(OverlayService);
  siteCurveQuery = inject(SiteCurveQuery);
  siteGroupQuery = inject(SiteGroupsQuery);
  siteGroupsService = inject(SiteGroupsService);

  private changeDetector = inject(ChangeDetectorRef);
  private eventService = inject(EventService);
  private mainQuery = inject(MainQuery);
  private siteCurveService = inject(SiteCurveService);
  private siteGroupsStore = inject(SiteGroupsStore);

  addedRowscounter = 0;
  editedRows = new Set<string>();
  gridAPI!: GridApi;
  gridAPI$ = new ReplaySubject<GridApi>(1);
  gridData$ = this.siteGroupQuery.selectAll().pipe(
    map((value) => {
      return value.map(({ description, id, name, rank_order }) => ({
        description,
        id,
        name,
        rank_order,
      }));
    })
  );
  isEditModeEnabled$ = new BehaviorSubject(false);
  gridOptions$: BehaviorSubject<GridOptions> = new BehaviorSubject({
    defaultColDef: {
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
      cellStyle: {
        overflow: 'hidden',
        'text-overflow': 'ellipsis',
        'white-space': 'nowrap',
      },
      editable: () => this.isEditModeEnabled$.getValue(),
      resizable: true,
    },
    ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
    columnDefs: [
      {
        field: 'id',
        headerName: 'id',
        hide: true,
      },
      {
        cellClass: 'group-actions',
        cellRenderer: AgActionsComponent,
        cellRendererParams: {
          deleteClickFN: ({ rowNode }: { rowNode: RowNode }) => {
            if (!rowNode.data.id) {
              this.gridAPI.applyTransaction({ remove: [rowNode.data] });

              if (this.addedRowscounter === 1) {
                this.reorderedRows.clear();
                this.removedRows = [];
                this.newRowAdded = false;
              } else {
                this.removedRows.pop();
              }

              this.addedRowscounter -= 1;
              this.checkChanges();
            } else {
              void this.dialogConfirmRemoveSiteGroup(rowNode);
            }
          },
        },
        editable: false,
        field: TableConstants.FIELDS.ACTIONS,
        headerName: '',
        hide: true,
        maxWidth: 75,
        minWidth: 75,
        rowDrag: true,
        suppressFillHandle: true,
        suppressSizeToFit: true,
      },
      {
        cellClass: 'text-left',
        cellClassRules: {
          'border-aux-error': (params) => params.data.showError,
        },
        field: 'name',
        headerName: 'Site Group',
        tooltipValueGetter: Utils.getDuplicateNameCellTooltip,
        valueSetter: (params: ValueSetterParams) => {
          params.data.name = params.newValue?.toString().trim() || '';

          return true;
        },
      },
      {
        cellClass: 'text-left',
        field: 'description',
        headerName: 'Description',
      },
    ],
    enableRangeSelection: true,
    enterNavigatesVertically: true,
    enterNavigatesVerticallyAfterEdit: true,
    getRowId: ({ data }) => data?.id || data?.randomID,
    headerHeight: 40,
    rowDragManaged: true,
    suppressCellFocus: false,
    suppressMenuHide: true,
    undoRedoCellEditing: true,
    undoRedoCellEditingLimit: 20,
  } as GridOptions);
  hasChanges = false;
  initialValues: SiteGroupModel[] = [];
  newRowAdded = false;
  removedRows: SiteGroupModel[] = [];
  reorderedRows = new Set<string>();
  reorderQueue: { ids: string; new_index: number }[] = [];
  valuesBeforeRemove: SiteGroupModel[] = [];

  cancelEditMode() {
    this.gridAPI.stopEditing();
    this.isEditModeEnabled$.next(false);

    if (this.gridAPI) {
      AgSetColumnsVisible({
        gridApi: this.gridAPI,
        keys: [TableConstants.FIELDS.ACTIONS],
        visible: false,
      });
    }

    this.gridAPI.sizeColumnsToFit();

    this.gridData$ = this.siteGroupQuery.selectAll().pipe(
      map((value) => {
        return value
          .map(({ description, id, name, rank_order }) => ({
            description,
            id,
            name,
            rank_order,
          }))
          .sort((a, b) => (a.rank_order > b.rank_order ? 1 : -1));
      })
    );

    this.resetChangeIndicators();
    this.gridAPI.redrawRows();
  }

  async canDeactivate(): Promise<boolean> {
    if (this.hasChanges) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  cellEditingStopped(event: CellEditingStoppedEvent): void {
    if (!event.newValue?.trim()) {
      this.getRowsErrors();
    }
  }

  cellValueChanged(event: CellValueChangedEvent) {
    if (event.data.id) {
      this.editedRows.add(event.data.id);
      this.checkChanges();
    }

    this.getRowsErrors();
  }

  checkChanges() {
    const currentValues = this.getCurrentValues();

    if (currentValues.length || this.removedRows.length) {
      this.hasChanges = !isEqual(this.initialValues, currentValues);
      this.changeDetector.detectChanges();
    }
  }

  constructor() {
    this.siteGroupsService.get().pipe(takeUntilDestroyed()).subscribe();
    this.siteCurveService.get().pipe(takeUntilDestroyed()).subscribe();

    this.gridData$.pipe(takeUntilDestroyed()).subscribe((gridData) => {
      this.initialValues = gridData.map((value) => {
        return { ...value, description: value?.description === '' ? null : value.description };
      });

      if (this.valuesBeforeRemove.length) {
        setTimeout(() => {
          this.gridAPI?.setGridOption('rowData', this.valuesBeforeRemove);
          this.valuesBeforeRemove = [];
        }, 0);
      }

      if (!this.hasChanges) {
        this.gridAPI?.setGridOption('rowData', this.valuesBeforeRemove);
      }

      this.checkChanges();
    });

    combineLatest([
      this.eventService.select$(EventType.TRIAL_CHANGED),
      this.mainQuery.select('trialKey'),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.initialValues = [];
        this.valuesBeforeRemove = [];
        this.hasChanges = false;
      });
  }

  dialogConfirmRemoveSiteGroup(rowNode: RowNode) {
    const site = this.siteCurveQuery.getAll();
    const curves = site.filter((el) => el.site_group_id === rowNode.data.id);

    const organizations_with_forecasts = [...curves].reduce((acc: Organization[], el) => {
      const organizations = el.organizations_with_forecasts;

      organizations.forEach((org: Organization) => {
        if (!acc.find((e) => e.id === org.id)) {
          acc.push(org);
        }
      });

      return acc;
    }, []);

    if (!curves.length) {
      this.overlayService.openPopup<
        { message: string },
        boolean | undefined,
        ConfirmationModalComponent
      >({
        content: ConfirmationModalComponent,
        data: {
          message: `Are you sure you want to remove ${rowNode.data.name}?`,
        },
        settings: {
          header: 'Remove Site Group',
          primaryButton: {
            action: (instance) => {
              this.removedRows.push(rowNode.data);
              this.gridAPI.applyTransaction({ remove: [rowNode.data] });
              this.valuesBeforeRemove = this.getCurrentValues();
              this.checkChanges();
              instance?.ref.close();
            },
            label: 'Remove',
          },
        },
      });

      return;
    }

    let list = `<br>Site Curves:<br><ul class="list-disc pl-4">${curves
      .map(
        ({ name }) =>
          `<li><a class="aux-link" href="/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.SITE_DRIVER.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.SITE_DRIVER.CURVES}">${name}</a></li>`
      )
      .join('')}</ul>`;

    if (organizations_with_forecasts.length) {
      list += `<br>Forecast Methodology:<br><ul class="list-disc pl-4">${organizations_with_forecasts
        .map(
          ({ name }) =>
            `<li><a class="aux-link" href="/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.FORECAST_METHODOLOGY}">${name}</a></li>`
        )
        .join('')}</ul>`;
    }

    this.overlayService.openPopup({
      data: {
        hideConfirmationHint: true,
        message: `${rowNode.data.name} cannot be deleted because it is being used in other areas of the application.<br>Please first remove the dependencies below and then ${rowNode.data.name} can be successfully deleted.<br>${list}`,
        skipKeywordConfirmation: true,
      },
      modal: ConfirmationActionModalComponent,
      settings: {
        header: 'Remove Site Group',
        primaryButton: {
          disabled: true,
          label: 'Remove',
        },
      },
    });
  }

  enableEditMode() {
    this.isEditModeEnabled$.next(true);
    AgSetColumnsVisible({
      gridApi: this.gridAPI,
      keys: [TableConstants.FIELDS.ACTIONS],
      visible: true,
    });
    this.gridAPI.sizeColumnsToFit();
    this.gridAPI.redrawRows();
    this.gridAPI.startEditingCell({
      colKey: 'name',
      rowIndex: 0,
    });
  }

  getCurrentValues() {
    const currentValues: SiteGroupModel[] = [];

    this.gridAPI?.forEachNode(({ data }) => {
      currentValues.push({
        ...data,
        description: data.description?.length === 0 ? null : data.description,
      });
    });

    return currentValues;
  }

  private getRowsErrors(): string[] {
    const errors: string[] = [];
    const uniqueNames = this.gridUniqueNames();

    this.gridAPI.forEachNode((node) => {
      const rowErrors = [];
      const { name } = node.data as SiteGroupModel;
      const trimmedName = name?.trim();

      if (!trimmedName) {
        rowErrors.push(FormControlConstants.VALIDATION_MESSAGE.REQUIRED_SITE_GROUP);
      }

      if (trimmedName && !uniqueNames.includes(trimmedName.toLowerCase())) {
        rowErrors.push(FormControlConstants.VALIDATION_MESSAGE.DUPLICATED_SITE_GROUP);
      }

      node.data.showError = !!rowErrors.length;
      errors.push(...rowErrors);
    });

    this.gridAPI.redrawRows();

    return uniq(errors);
  }

  private gridUniqueNames(): string[] {
    const arr: string[] = [];

    this.gridAPI.forEachNode((node) => {
      arr.push(node.data?.name?.trim().toLowerCase() || '');
    });

    return arr.filter((el) => arr.indexOf(el) === arr.lastIndexOf(el));
  }

  onAddSiteGroups() {
    const ss = this.gridAPI.applyTransaction({
      add: [{ randomID: Utils.uuid() }],
    });

    if (ss?.add.length && ss.add[0].rowIndex) {
      this.gridAPI.startEditingCell({
        colKey: 'name',
        rowIndex: ss.add[0].rowIndex,
      });
    }

    this.addedRowscounter += 1;
    this.newRowAdded = true;
    this.checkChanges();
  }

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI$.next(api);
    this.gridAPI = api;
    api.sizeColumnsToFit();
  }

  onRowDragEnd(event: RowDragEndEvent) {
    if (event.node.data?.id && event.node.rowIndex !== null) {
      this.reorderQueue.push({
        ids: event.node.data.id,
        new_index: event.node.rowIndex + 1,
      });
      this.reorderedRows.add(event.node.data.id);
      this.checkChanges();
    }
  }

  // eslint-disable-next-line max-lines-per-function
  onSaveAll = async () => {
    if (!this.getRowsErrors().length) {
      const updateData: SiteGroupModel[] = [];
      const insertData: SiteGroupModel[] = [];
      const reorderData: SiteGroupModel[] = [];

      this.siteGroupsStore.setLoading(true);

      if (
        this.reorderQueue.length < this.gridAPI.getDisplayedRowCount() &&
        !this.removedRows.length
      ) {
        this.reorderQueue.forEach(({ ids, new_index }) => {
          const pp = this.siteGroupQuery.getEntity(ids);

          if (pp) {
            const { description, id, name } = pp;

            updateData.push({
              description,
              id,
              name,
              rank_order: new_index,
            });
          }
        });
        this.gridAPI.forEachNode((node) => {
          const rowIndex = (node.rowIndex || 0) + 1;

          const { description, id, name } = node.data as RequireSome<
            SiteGroupModel,
            'description' | 'id' | 'name' | 'rank_order'
          >;

          if (!id && !this.editedRows.has(id)) {
            insertData.push({
              description,
              id,
              name,
              rank_order: rowIndex,
            });
          }

          if (id && this.editedRows.has(id)) {
            updateData.push({
              description,
              id,
              name,
              rank_order: rowIndex,
            });
          }

          if (id) {
            reorderData.push({
              description,
              id,
              name,
              rank_order: rowIndex,
            });
          }
        });
      } else {
        this.gridAPI.forEachNode((node) => {
          const rowIndex = (node.rowIndex || 0) + 1;
          const { description, id, name } = node.data as RequireSome<
            SiteGroupModel,
            'description' | 'id' | 'name' | 'rank_order'
          >;

          if (
            this.reorderQueue.length ||
            !id ||
            this.editedRows.has(id) ||
            this.removedRows.length
          ) {
            updateData.push({
              description,
              id,
              name,
              rank_order: rowIndex,
            });
          }

          if (!id && !this.editedRows.has(id)) {
            insertData.push({
              description,
              id,
              name,
              rank_order: rowIndex,
            });
          }

          if (id) {
            reorderData.push({
              description,
              id,
              name,
              rank_order: rowIndex,
            });
          }
        });
      }

      let hasSuccess = true;

      if (updateData.length) {
        for (const { description, id, name, rank_order } of reorderData) {
          await this.siteGroupsService.update({ description, id, name, rank_order });
        }

        await firstValueFrom(this.siteCurveService.get().pipe(take(1)));
      }

      if (insertData.length) {
        for (const patientGroup of insertData) {
          hasSuccess = (await this.siteGroupsService.add(patientGroup)).success;
        }
      }

      if (this.removedRows.length) {
        for (const { description, id, name, rank_order } of this.removedRows) {
          await this.siteGroupsService.remove({ description, id, name, rank_order });
        }
      }

      if (hasSuccess) {
        this.overlayService.success(`Successfully Saved`);

        this.cancelEditMode();
      } else {
        this.overlayService.error(`An error has occurred`);
      }
    } else {
      this.overlayService.error(this.getRowsErrors());
    }

    this.siteGroupsStore.setLoading(false);
  };

  resetChangeIndicators() {
    this.reorderedRows.clear();
    this.editedRows.clear();
    this.removedRows = [];
    this.reorderQueue = [];
    this.newRowAdded = false;
    this.addedRowscounter = 0;
    this.hasChanges = false;
    this.initialValues = [];
    this.valuesBeforeRemove = [];
  }
}
